import jwt from 'jsonwebtoken'
import { AppDispatch } from '@/store'
import { UserServiceHelper, UserTypes } from '@/modules/User'
import { InsurancesTypes } from '@/modules/Insurances'

import AuthApiSingleton from './AuthApi'
import { ConfigurationServiceHelper } from '../Configuration'
import { DiscussionsTypes } from '../Discussions'
import { PendingAcquisitionUser } from '@/dataV3/User/PendingAcquisitionUser'
import { _getItem, _removeItem, _saveAuthInfo, TokenKeys } from '@/modules/Auth/LocalStorageHelper'
import { loginAsync, refreshTokenAsync, requestAuthenticatedV2Async } from '@/modules/Auth/BasicAuthServiceHelper'
import { AuthRawReturn } from '@/modules/Auth/apiTypes/base'
import { Login, MagicLink, Register } from './apiTypes'
import { FavoritesTypes } from '@/modules/Favorites'
import { ApiNotificationsTypes } from '@/modules/ApiNotifications'
import { NotificationsTypes } from '@/modules/Notifications'
import { CartTypes } from '@/modules/Cart'
import { PetsitterUserTypes } from '@/modules/PetsitterUser'
import { CurrentAccount } from '../User/apiTypes'
import { runWithEntityManagerClosedRetryAsync } from './EntityManagerClosedRetryHelper'

export { isEntityManagerClosedError } from './EntityManagerClosedRetryHelper'

export const dispatchCheckAuthStatusAsync =
  (dispatch: AppDispatch) =>
  async (refreshUser = false) => {
    // First check authToken expiration from localStorage or sessionStorage
    const authToken = _getItem(TokenKeys.Auth)

    let isValid = false
    if (authToken) {
      const decodedToken = jwt.decode(authToken, { complete: true })
      const payload = decodedToken?.payload as jwt.JwtPayload // Force cast because we asked `complete: true` in decode
      const dateNow = new Date()

      if (payload.exp === undefined || payload.exp * 1000 >= dateNow.getTime()) {
        isValid = true
      }
    }

    // If not valid, try to refreshToken if exists in either storage
    if (!isValid) {
      const refreshToken = _getItem(TokenKeys.Refresh)

      if (refreshToken) {
        try {
          const { token } = await refreshTokenAsync(refreshToken)
          isValid = !!token.length
        } catch (err) {
          isValid = false
          console.warn('Cannot refresh token', err)
        }
      }
    }

    let user: CurrentAccount.Get.Response | null = null
    if (isValid && refreshUser) {
      try {
        user = await UserServiceHelper.dispatchGetMyInfoAsync(dispatch)()
        isValid = !!user
      } catch (error) {
        isValid = false
        console.warn('Error refreshing user info:', error)
      }
    }

    return { isValid, user }
  }

export const dispatchLogoutAsync = (dispatch: AppDispatch) => async (): Promise<boolean> => {
  _removeItem(TokenKeys.Auth)
  _removeItem(TokenKeys.Refresh)
  _removeItem(TokenKeys.Mercure)
  _removeItem(TokenKeys.MercureUrl)

  dispatch({ type: UserTypes.Logout, payload: null })
  dispatch({ type: InsurancesTypes.Clean, payload: null })
  dispatch({ type: DiscussionsTypes.Clean, payload: null })
  dispatch({ type: ApiNotificationsTypes.Cleanup, payload: null })
  dispatch({ type: NotificationsTypes.Cleanup, payload: null })
  dispatch({ type: FavoritesTypes.Logout, payload: null })
  dispatch({ type: CartTypes.Clean, payload: null })
  dispatch({ type: PetsitterUserTypes.Clean, payload: null })

  return true
}

export const dispatchRegisterAsync =
  (dispatch: AppDispatch) =>
  async ({ type, body }: Register.Body): Promise<PendingAcquisitionUser> => {
    // Decide which API method to call based on `type`
    const response = await runWithEntityManagerClosedRetryAsync(async () => {
      let registerResponse: Register.Response | undefined

      switch (type) {
        case 'petowner':
          registerResponse = await requestAuthenticatedV2Async<Register.Response, Register.PetOwnerBody>(
            Register.PetOwnerRoute,
            { body },
            { doNotAuthenticate: true },
          )
          break
        case 'borrower':
          registerResponse = await requestAuthenticatedV2Async<Register.Response, Register.BorrowerBody>(
            Register.BorrowerRoute,
            { body },
            { doNotAuthenticate: true },
          )
          break
        case 'petsitter':
          registerResponse = await requestAuthenticatedV2Async<Register.Response, Register.PetSitterBody>(
            Register.PetSitterRoute,
            { body },
            { doNotAuthenticate: true },
          )
          break
      }

      if (!registerResponse) {
        throw new Error(`Unknown type: ${type}`)
      }

      return registerResponse
    }, 'dispatchRegisterAsync')

    const { token, refreshToken, mercureToken, mercurePublishUrl, ...user } = response

    _saveAuthInfo({
      token,
      refreshToken,
      mercureToken,
      mercurePublishUrl,
    })

    dispatch({ type: UserTypes.UpdateUser, payload: user })

    // Refresh all configurations
    await ConfigurationServiceHelper.dispatchRefreshAllConfigurationAsync(dispatch)()

    return user
  }

const _finishAuthRawReturn = (dispatch: AppDispatch) => async (response: AuthRawReturn) => {
  const {
    token,
    refresh_token: refreshToken,
    mercure_token: mercureToken,
    mercure_url: mercurePublishUrl,
    reactivation,
  } = response
  _saveAuthInfo({
    token,
    refreshToken,
    mercureToken,
    mercurePublishUrl,
  })

  const user = await UserServiceHelper.dispatchGetMyInfoAsync(dispatch)(
    reactivation !== undefined ? { reactivation } : undefined,
  )

  // Refresh all configurations
  await ConfigurationServiceHelper.dispatchRefreshAllConfigurationAsync(dispatch)()

  return user
}

export const dispatchLoginAsync = (dispatch: AppDispatch) => async (body: Login.Body) => {
  const response = await runWithEntityManagerClosedRetryAsync(async () => loginAsync(body), 'dispatchLoginAsync')

  return await _finishAuthRawReturn(dispatch)(response)
}

export const dispatchMagicLinkSubmitAsync = (dispatch: AppDispatch) => async (body: MagicLink.SubmitBody) => {
  const response = await runWithEntityManagerClosedRetryAsync(async () => {
    return AuthApiSingleton.requestAsync<MagicLink.SubmitResponse, typeof body>(MagicLink.SubmitRoute, {
      body,
    })
  }, 'dispatchMagicLinkSubmitAsync')

  return await _finishAuthRawReturn(dispatch)(response)
}
